import { Component, OnDestroy, OnInit } from '@angular/core';
import { DataManipulationService } from 'src/app/services/data-manipulation.service';
import { ToServiceService } from '../services/to-service.service';
import {BehaviorSubject, concatMap, EMPTY, expand, finalize, forkJoin, from, map, mergeMap, Observable, of, Subscription, switchMap, tap} from 'rxjs';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import {ApiwrapperService} from "../../../services/apiwrapper.service";
import { TitleCasePipe } from '@angular/common';

interface SortingByTeamType {
  name: string,
  icon: string,
  type: string,
  show: boolean
}

interface SortingByIndicators {
  name: string,
  icon: string,
  type: string
}

interface ViewChanges {
  isLastDaysChanged: boolean,
  changedLastDays: number,
  isIndicatorChanged: boolean,
  changedIndicator: any,
  isFilterScoreChanged: boolean,
  changedScoreFilter: any,
  isTeamListChanged: boolean,
  changedTeamList: any[]
}
export const DEFAULT_VIEW_CHANGES: ViewChanges = {
  isLastDaysChanged: false,
  changedLastDays: 0,
  isIndicatorChanged: false,
  changedIndicator: null,
  isFilterScoreChanged: false,
  changedScoreFilter: null,
  isTeamListChanged: false,
  changedTeamList: []
}
@Component({
  selector: 'app-new-teams-overview',
  templateUrl: './new-teams-overview.component.html',
  styleUrls: ['./new-teams-overview.component.scss']
})

export class NewTeamsOverviewComponent implements OnInit, OnDestroy {
  teams: any[] = [];
  backupTeams: any[] = [];
  defaultTeamsForFilters: any[] = [];
  teamsForFilterDropdown: any[] = [];
  indicatorInterval: any[] = [];
  uncheckedTeams: any[] = [];
  userViews: any[] = [];
  membersListForViewShare: any[] = [];
  initialViewsState: any[] = [];
  sortingByIndicators: SortingByIndicators[] = [
    {name: 'Overall', icon: 'overall', type: ''},
    {name: 'Performance', icon: 'performance', type: ''},
    {name: 'Motivation', icon: 'motivation', type: ''},
    {name: 'Satisfaction', icon: 'satisfaction', type: ''},
    {name: 'Stress', icon: 'stress', type: ''},
  ];
  sortingByTeamType: SortingByTeamType[] = [];
  defaultTeams: any[] = [];
  availableTeams: any[] = [];

  sortByTeamType: SortingByTeamType | any;

  titleCasePipe = new TitleCasePipe();

  showTeams: boolean = false;
  sortedAtleastOnce: boolean = false;
  selectedViewChangesMade: boolean = false;

  defaultSwitchDays: number = 30;
  defaultTeamLimitOnPage: number = 5;
  defaultPageNumber: number = 1;

  indicatorToShow: any | null = null;
  aggregatedScoresLatest: any | null = null;
  aggregatedScoresPast: any | null = null;
  aggregatedScores: any | null = null;
  headerFiltersByView: any | null = null;
  selectedView: any | null = null;
  viewChanges: ViewChanges = {...DEFAULT_VIEW_CHANGES};

  filterByScore: string = 'ASC';
  defaultIndicatorToShow: string = '';

  private intervalsSub!: Subscription;
  private teamMembersSub!: Subscription;
  private pollDetailssSub!: Subscription;
  private viewsSub!: Subscription;

  constructor(private data: DataManipulationService, private toService: ToServiceService, private router: Router, private translate: TranslateService, private api: ApiwrapperService) { }

  ngOnDestroy(): void {
    this.intervalsSub?.unsubscribe();
    this.teamMembersSub?.unsubscribe();
    this.pollDetailssSub?.unsubscribe();
    this.viewsSub?.unsubscribe();
  }

  ngOnInit(): void {
    this.indicatorToShow = this.indicatorToShow === null ? 'teamSuccessteamScore':this.indicatorToShow;
    this.defaultIndicatorToShow = 'score';
    this.getTOViews();
    this.getUserAvailableTeams().pipe(
      concatMap(() => this.getUserTeams())
    ).subscribe();
    this.getInterval();
  }

  private getUserAvailableTeams(): Observable<any> {
    this.availableTeams = [];
    return this.toService.getUserAvailableTeams().pipe(
      map((res: any) => res._embedded.teams ?? []),
      map((teams: any[]) => 
        teams.map(team => ({...team, checked: true, searched : true}))
      ),
      tap((res: any) => {
        this.availableTeams = [...res];
      })
    )
  }

  private getUserTeams(): Observable<any[]> {
    this.teams = [];
    this.backupTeams = [];
    this.defaultTeamsForFilters = [];
    this.uncheckedTeams = [];
    return this.toService.getTOTeams(this.defaultSwitchDays, this.defaultIndicatorToShow, this.filterByScore, this.defaultPageNumber, this.defaultTeamLimitOnPage, this.defaultTeams).pipe(
      tap(res => {
        this.teams = Array(res.totalItems).fill(null).map(() => {
          const isLoadingSubject = new BehaviorSubject<boolean>(false);
          return {
            checked: true,
            isLoading$: isLoadingSubject.asObservable(),
            _isLoading: isLoadingSubject
          }
        });
        this.backupTeams = [...this.teams];
        this.defaultTeamsForFilters = this.teams.map((team: any) => ({...team}));
      }),
      expand((res: any) => {
        if (res.page < res.pageCount) {
          return this.toService.getTOTeams(this.defaultSwitchDays, this.defaultIndicatorToShow, this.filterByScore, res.page + 1, this.defaultTeamLimitOnPage, this.defaultTeams)
        }
        return EMPTY;
      }),
      mergeMap((res: any) => from(res.teams)),
      mergeMap((team: any, index: number) => 
        this.processReport(team, index), 5),
      finalize(() => {
        this.backupTeams = [...this.teams];
        this.defaultTeamsForFilters = this.teams.map((team: any) => ({...team}));
      })
    );
  }

  private getTOViews() {
    this.viewsSub = this.toService.getTOViews().subscribe((res: any) => {
      const views = res._embedded.views;
      if (views.length) {
        views.forEach((view: any) => {
          this.processViews(view);
        })
      }
      this.initialViewsState = this.userViews.map((view: any) => ({...view}));
    });
  }

  private processViews(view: any) {
    this.userViews = [...this.userViews, {
        viewUuid: view.uuid,
        viewTitle: view.name,
        byScore: view.filters.byScore,
        byIndicator: view.filters.byIndicator,
        byLastDays: view.filters.byLastDays,
        byTeams: view.filters.teams,
        creator: view.creator,
        sharedUsers: [],
        openSettings: view.openSettings || false,
        openCreatorView: false,
        creatorPermission: this.checkViewCreatorPermission(view)
      }];
  }

  checkViewCreatorPermission(viewToCheck: any) {
    const stored = localStorage.getItem('userDetails');
    const currentUser = stored ? JSON.parse(stored) : null;

    return viewToCheck.creator.uuid === currentUser.uuid;
  }

  private getInterval() {
    this.indicatorInterval = [];
    this.intervalsSub = this.toService.getIndicatorInterval().subscribe((res:any) => {
      this.indicatorInterval = [...res];
    });
  }

  private getTeamMembers(team: any) {
    this.teamMembersSub = this.toService.getMemberActivity(team.teamUuid).subscribe((res:any) => {
      team.members = [];
      if (res.teamMembersReport.length > 0) {
        res.teamMembersReport.forEach((member:any, index:number) => {
          if (index < (res.teamMembersReport.length - 1)) {
            team.members.push(member.teamMember);
          } else {
            team.members.push(member.teamMember);
            team.gotMembers = true;
          }
        });
      } else {
        team.gotMembers = true;
      }
      window.dispatchEvent(new Event('resize'));
    });
  }

  createNewView(event: any) {
    if (event.newView) {
      this.indicatorToShow = 'teamSuccessteamScore';
      this.toService.createView(
        { name:event.title,
          teams: this.availableTeams.filter((team: any) => !(this.uncheckedTeams.some((uncheckedTeam: any) => uncheckedTeam.uuid === team.uuid))).map((team:any) => team.uuid) || [],
          users: [],
          filters: {
            byScore: event.byScore,
            byIndicator: this.indicatorToShow,
            byLastDays: this.defaultSwitchDays
          }
          }).subscribe((res: any) => {
            res.openSettings = true;
            this.processViews(res);
            this.initialViewsState = this.userViews.map((view: any) => ({...view}));
            this.selectedView && (this.selectedView = null);
            this.setSelectedView(this.userViews.find((view: any) => view.viewUuid === res.uuid));
          });
    }
  }

  searchMemberForView(event: any) {
    this.toService.searchWorkspaceMemberForViewShare(event).subscribe((res: any) => {
      this.membersListForViewShare = [...res];
    });
  }

  shareViewWithMember(event: any) {
    this.toService.shareViewWithUser({viewUuid: event.view.viewUuid, userUuid: event.member.user.uuid}).subscribe((res: any) => {
      event.view.sharedUsers = [...event.view.sharedUsers, res];
    });
  }

  removeMemberFromView(event: any) {
    this.toService.removeShareFromUser(event.memberUuid).subscribe((res: any) => {
      event.view.sharedUsers = event.view.sharedUsers.filter((user: any) => user.uuid !== event.memberUuid);
    });
  }

  setSelectedView(event: any) {
    this.defaultTeams = [];
    if (event) {
      const totalTeamsSet = new Set(this.availableTeams.map(t => t.uuid));
      const viewTeamsSet = new Set(event.byTeams);
      this.selectedView = event;
      this.viewChanges = {...DEFAULT_VIEW_CHANGES};
      this.defaultTeams = [...event.byTeams];
      this.filterByScore = event.byScore;
      this.uncheckedTeams = [
        ...this.availableTeams.filter(t => !viewTeamsSet.has(t.uuid)), 
        ...this.defaultTeams.filter(t => !totalTeamsSet.has(t))
      ];

      if (event.sharedUsers.length === 0) {
        this.toService.getUsersForSharedView(event.viewUuid).subscribe((res: any) => {
          event.sharedUsers = [...res._embedded.permissions];
        });
      }
      this.headerFiltersByView = {indicator: event.byIndicator, days: event.byLastDays};
    } else {
      this.selectedView = null;
      this.headerFiltersByView = {indicator: 'teamSuccessteamScore', days: 30};
    }
  }

  patchView(event: any) {
    let viewIndex: number = 0;
    this.toService.patchView(event.view.viewUuid, event.body).subscribe((res:any) => {
      if (!event.body.name) {
        this.userViews = this.userViews.filter((view: any) => view.viewUuid !== res.uuid);
        this.processViews(res);
        this.initialViewsState = this.userViews.map((view: any) => ({...view}));
        viewIndex = this.userViews.findIndex((view: any) => view.viewUuid === res.uuid);
        this.setSelectedView(this.userViews[viewIndex]);
        this.checkViewChanges();
        this.data.hideLoading();
      }
    });
  }

  deleteView(event: any) {
    this.toService.deleteView(event).subscribe((res:any)=>{
      this.userViews = this.userViews.filter((view: any) => view.viewUuid !== event);
      this.setSelectedView(null);
    });
  }

  checkViewChanges() {
    if (this.selectedView !== null) {
      const initial = this.initialViewsState.find((initial: any) => initial.viewUuid === this.selectedView.viewUuid);
      const initialViewTeams = new Set(initial.byTeams);

      this.viewChanges.isLastDaysChanged = this.defaultSwitchDays !== initial.byLastDays;
      this.viewChanges.changedLastDays = this.viewChanges.isLastDaysChanged ? this.defaultSwitchDays : 0;
      this.viewChanges.isIndicatorChanged = this.indicatorToShow !== initial.byIndicator;
      this.viewChanges.changedIndicator = this.viewChanges.isIndicatorChanged ? this.indicatorToShow : '';
      this.viewChanges.isFilterScoreChanged = this.filterByScore !== initial.byScore;
      this.viewChanges.changedScoreFilter = this.viewChanges.isFilterScoreChanged ? this.filterByScore : '';
      this.viewChanges.isTeamListChanged =
        this.teams.filter((team: any) => team.checked).length !== initial.byTeams.length ||
        this.teams.filter((team: any) => {return team.checked && initialViewTeams.has(team.teamUuid) || false;}).length !== initial.byTeams.length;
      this.viewChanges.changedTeamList = this.viewChanges.isTeamListChanged ? [...this.teams.filter((team: any) => team.checked).map((team: any) => team.teamUuid)]:[];

      this.selectedViewChangesMade = this.viewChanges.isIndicatorChanged || this.viewChanges.isFilterScoreChanged || this.viewChanges.isTeamListChanged || this.viewChanges.isLastDaysChanged;
    }
  }

  saveViewChanges(event: any) {
    if (event) {
      this.data.showLoading();
      this.patchView({
        view: this.selectedView,
        body: {
          ...(this.viewChanges.isTeamListChanged && this.viewChanges.changedTeamList.length ? {teams: this.viewChanges.changedTeamList}:{}),
          ...(this.viewChanges.isIndicatorChanged || this.viewChanges.isFilterScoreChanged || this.viewChanges.isLastDaysChanged ?
            {filters: {
              byScore: this.viewChanges.isFilterScoreChanged ? this.viewChanges.changedScoreFilter:this.selectedView.byScore,
              byIndicator: this.viewChanges.isIndicatorChanged ? this.viewChanges.changedIndicator:this.selectedView.byIndicator,
              byLastDays: this.viewChanges.isLastDaysChanged ? this.viewChanges.changedLastDays:this.selectedView.byLastDays
            }}:{})
        }
      });
    }
  }

  removeMember(event: any) {
    if (event.type === 'team') {
      this.toService.removeMemberFromTeam(event.member.uuid, event.team.teamUuid).subscribe((res:any) => {
        event.team.members = event.team.members.filter((member: any) => member.uuid !== event.member.uuid);
        this.data.showAlert(res.info.messages[0]);
      });
    } else if (event.type === 'workspace') {
      this.toService.removeMemberFromWorkspace(event.member.user.uuid).subscribe((res:any) => {
        event.team.members = event.team.members.filter((member: any) => member.uuid !== event.member.uuid);
        this.data.showAlert(res.info.messages[0]);
      });
    }
  }

  private getPollDetails(team: any) {
    team.currentPollDetails = null;
    this.pollDetailssSub = this.toService.getTeamActivePollingDetails(team.teamUuid).subscribe((res:any) => {
      if (!res.info) {
        team.currentPollDetails = res;
      } else {
        team.currentPollDetails = {status: 'inactive'};
      }
      this.getTeamMembers(team);
    });
  }

  pauseFeedbackLoop(team: any) {
    this.toService.pauseTeamActivePolling(team.teamUuid, team.currentPollDetails.uuid, 'paused').subscribe((res: any) => {
      team.currentPollDetails = res;
      team.teamHaveActivePolling = 'no';
      team.showQuickActions = false;
    });
  }

  getAggregatedScores(event: any) {
    const checkedTeams = this.teams.length ? this.teams.filter((team: any) => team.checked).map((team: any) => team.teamUuid) : [];
    if (checkedTeams.length) {
      this.toService.getAggregatedScores(checkedTeams).subscribe((res:any) => {
        this.aggregatedScoresLatest = res.latest;
        this.aggregatedScoresPast = res.past;
        this.processAggregatedScores();
      });
    }
  }

  processAggregatedScores() {
    Object.keys(this.aggregatedScoresLatest).forEach((el) => {
      this.aggregatedScoresLatest[el] = Math.round(this.aggregatedScoresLatest[el]);
        this.indicatorInterval.forEach((interval) => {
          let numb = this.aggregatedScoresLatest[el];
          if (interval.min <= numb && numb <= interval.max) {
            // @ts-ignore
            this.aggregatedScoresLatest[el.replace('Score', '').toLowerCase() + 'Color'] = this.data.colors[interval.indicatorColor.color];
            //@ts-ignore
            this.aggregatedScoresLatest[el.replace('Score', '').toLowerCase() + 'Gradient'] = this.data.colors[interval.indicatorColor.color+'Gradient'];
          } else if (numb === 0) {
            this.aggregatedScoresLatest[el.replace('Score', '').toLowerCase() + 'Color'] = this.data.colors.grey;
          }
        });
      });
      this.aggregatedScores = {latest: this.aggregatedScoresLatest, past: this.aggregatedScoresPast};
  }

  setTeamReportIntervalsAndColors(team: any): Observable<any> {
    return of(team).pipe(
      map((team: any) => {
        Object.keys(team.teamReport).forEach((el) => {
          this.indicatorInterval.forEach((interval) => {
            let numb = team.teamReport[el];
            if (interval.min <= numb && numb <= interval.max) {
              // @ts-ignore
              team.teamReport[el.toLowerCase() + 'Color'] = this.data.colors[interval.indicatorColor.color];
            } else if (numb === 0) {
              team.teamReport[el.toLowerCase() + 'Color'] = this.data.colors.grey;
            }
          });
        });
        return team;
      })
    );
  }

  setTeamQuestionCollectionType(team: any): Observable<any> {
    return of(team).pipe(
      map((team: any) => {
        if (team?.questionCollections && team?.questionCollections.length > 0) {
          team.questionCollections.forEach((el: any) => {
            switch (el.slug) {
              case 'hybrid':
                team.type = this.translate.instant('workspace_team_type_hybrid');
                team.typeIcon = 'hybrid';
                return;
              case 'remote':
                team.type = this.translate.instant('workspace_team_type_remote');
                team.typeIcon = 'remote';
                return;
              case 'office':
                team.type = 'Office';
                team.typeIcon = 'office';
                return;
              case 'physical-workplace':
                team.type = this.translate.instant('workspace_team_type_physical_workplace');
                team.typeIcon = 'office';
                return;
            }
          })
        }
        return team;
      })
    );
  }

  private processReport(team: any, index: number): Observable<any> {
    const THIRTY_DAYS = 30 * 24 * 60 * 60 * 1000;
    return of(team).pipe(
      concatMap((team) => this.setTeamReportIntervalsAndColors(team)),
      concatMap((team) => this.setTeamQuestionCollectionType(team)),
      map(team => ({
        ...team,
        showTeam: true,
        gotMembers: false,
        showQuickActions : false,
        searched : true,
        newTeam : (Date.now() - new Date(team.created.date).getTime()) <= THIRTY_DAYS,
        pausedTeam: team.teamHaveActivePolling === 'no' && team.previousTeamReport?.eligibleForResult,
        notEligibleTeam: team.teamHaveActivePolling === 'yes' && !team.teamReport.eligibleForResult,
        checked : this.uncheckedTeams.length ? !(this.uncheckedTeams.some((uncheckedTeam: any) => uncheckedTeam.teamUuid === team.teamUuid)) : true,
        trend: team.teamHaveActivePolling === 'yes' && team.previousTeamReport ? this.calculateTeamTrend(team.teamReport, team.previousTeamReport, this.indicatorToShow !== null ? this.indicatorToShow : 'teamSuccessteamScore'):null
      })),
      tap(team => {
        const placeholder = this.teams[index];
        team._isLoading = placeholder._isLoading;
        team.isLoading$ = placeholder.isLoading$;
        this.teams[index] = team;
        this.defaultTeamsForFilters[index].checked = true;
        this.teams[index]._isLoading.next(true);
      })
    );
  }

  openedQuickActions(openedTeam: any) {
    this.getPollDetails(openedTeam);
  }

  gotToTeamDashboard(teamUuid: any) {
    this.router.navigate(['/team/'+teamUuid]);
  }

  changeSwitchDaysNumber(days: number) {
    if (this.defaultSwitchDays !== days) {
      this.defaultSwitchDays = days;
      this.getUserTeams().pipe(
        finalize(() => {
          this.recalculateTeamTrendBasedOnIndicator(this.indicatorToShow);
        })
      ).subscribe();
      if (this.selectedView !== null) {
        this.checkViewChanges();
      }
    }
  }

  teamCheckorUncheck(event: {teamUuids: string[]; checked: boolean}) {
    const uuids = new Set(event.teamUuids);
    const missingUuids = event.teamUuids.filter(uuid => !this.teams.some(t => t.teamUuid === uuid));
    this.teams.forEach(team => {
      if (uuids.has(team.teamUuid)) {
        team.checked = event.checked;
      }
    });

    if (!event.checked) {
      this.uncheckedTeams = [
        ...this.uncheckedTeams,
        ...event.teamUuids.map(teamUuid => ({ teamUuid }))
      ];
    } else {
      this.uncheckedTeams = this.uncheckedTeams.filter(
        team => !uuids.has(team.teamUuid)
      );
    }    

    if (missingUuids.length && event.checked) {
      this.defaultTeams = [...this.defaultTeams, ...missingUuids];
      this.getUserTeams().pipe(
        finalize(() => {
          this.recalculateTeamTrendBasedOnIndicator(this.indicatorToShow);
        })
      ).subscribe(() =>{
        if (this.selectedView !== null) {
          this.checkViewChanges();
        }
        return;
      });
    }

    if (this.selectedView !== null) {
      this.checkViewChanges();
    }
  }

  recalculateTeamTrendBasedOnIndicator(indicator: string) {    
    this.teams.forEach((team:any) => {
      team.trend = this.calculateTeamTrend(team.teamReport, team.previousTeamReport, indicator);
    });
  }

  calculateTeamTrend(currentReport: any, previousReport: any, indicator: string) {
    return currentReport[indicator] === previousReport[indicator] ? null :
                            currentReport[indicator] > previousReport[indicator] ? 'up' :
                            currentReport[indicator] < previousReport[indicator] ? 'down':null
  }

  sortTeams(indicatorToSort: string) {
    this.defaultIndicatorToShow = indicatorToSort !== 'score' ? indicatorToSort+'Score':indicatorToSort;
    this.indicatorToShow = indicatorToSort === 'score' ? 'teamSuccessteamScore' : this.titleCasePipe.transform(indicatorToSort);
    this.getUserTeams().pipe(
      finalize(() => {
        this.recalculateTeamTrendBasedOnIndicator(this.indicatorToShow);
      })
    ).subscribe(() => {
      if (this.selectedView !== null) {
        this.checkViewChanges();
      }
    });
  }

  sortTeamsByScore(pickedValue: string) {
    this.filterByScore = pickedValue;
    this.sortedAtleastOnce = true;
    this.getUserTeams().pipe(
      finalize(() => {
        this.recalculateTeamTrendBasedOnIndicator(this.indicatorToShow);
      })
    ).subscribe(() => {
      if (this.selectedView !== null) {
        this.checkViewChanges();
      }
    });
  }
}
